import { getCookie, setCookie, clearCookie } from '../contexts/ThemeContext';
import { safeStorage, safeCookies, isTrackingBlocked } from '../utils/trackingUtils';
import type { UserPreferences as SharedUserPreferences } from '../../shared/types';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
export interface UserPreferences {
    theme: 'light' | 'dark' | 'auto';
    language: string;
    timezone: string;
    dateFormat: string;
    currency: string;
    notifications: {
        email: boolean;
        sms: boolean;
        push: boolean;
        training_reminders: boolean;
        event_updates: boolean;
        marketing: boolean;
    };
    training: {
        preferred_topics: string[];
        learning_style: 'visual' | 'auditory' | 'kinesthetic' | 'reading';
        difficulty_level: 'beginner' | 'intermediate' | 'advanced';
        session_duration: number; // minutes
        auto_play_videos: boolean;
        show_transcripts: boolean;
    };
    accessibility: {
        font_size: 'small' | 'medium' | 'large';
        high_contrast: boolean;
        reduce_motion: boolean;
        screen_reader_friendly: boolean;
    };
    privacy: {
        profile_visibility: 'public' | 'private' | 'agency_only';
        show_online_status: boolean;
        allow_messaging: boolean;
        data_sharing: boolean;
    };
}
export interface UserBehavior {
    last_visited_pages: string[];
    favorite_categories: string[];
    search_history: string[];
    training_progress: Record<string, number>;
    time_spent_on_site: number;
    preferred_content_types: string[];
    device_preferences: {
        mobile_optimized: boolean;
        desktop_preferred: boolean;
    };
}
export interface SessionData {
    session_id: string;
    start_time: number;
    last_activity: number;
    page_views: number;
    events_triggered: string[];
    referral_source: string;
    utm_parameters: Record<string, string>;
    device_info: {
        user_agent: string;
        screen_resolution: string;
        viewport_size: string;
        device_type: 'mobile' | 'tablet' | 'desktop';
    };
}
export interface PersonalizationData {
    user_segment: string;
    interests: string[];
    behavior_patterns: string[];
    recommended_content: string[];
    personalized_features: string[];
}
/**
 * Comprehensive cookie and user preference management service
 * Handles user preferences, behavior tracking, session management, and personalization
 * with advanced tracking prevention and performance optimization
 */
class CookieService {
    // Event listeners for cleanup
    private cleanupListeners: Array<() => void> = [];
    private readonly cookiePrefix = 'apt_';
    private readonly preferencesKey = `${this.cookiePrefix}preferences`;
    private readonly behaviorKey = `${this.cookiePrefix}behavior`;
    private readonly sessionKey = `${this.cookiePrefix}session`;
    private readonly personalizationKey = `${this.cookiePrefix}personalization`;
    // Enhanced batching and caching to reduce storage access
    private cache = new Map<string, any>();
    private batchQueue: Array<{
        key: string;
        value: any;
        operation: 'set' | 'clear';
    }> = [];
    private batchTimeout: NodeJS.Timeout | null = null;
    private readonly batchDelay = 300; // Increased to 300ms for better batching
    private readonly maxBatchSize = 25; // Increased batch size
    private readonly maxStorageAccessPerMinute = 20; // Reduced from 30 to 20
    private readonly storageCooldownPeriod = 2000; // 2 second cooldown when throttled
    // Track storage access to prevent excessive calls
    private storageAccessCount = 0;
    private lastStorageAccessTime = Date.now();
    private isThrottled = false;
    private throttleStartTime = 0;
    /**
     * Check if we should throttle storage access to prevent excessive calls
     * @returns {boolean} True if storage access should be throttled
     */
    private shouldThrottleStorage(): boolean {
        const now = Date.now();
        // Check if tracking is blocked first
        if (isTrackingBlocked()) {
            return true;
        }
        // Reset counter if a minute has passed
        if (now - this.lastStorageAccessTime > 60000) {
            this.storageAccessCount = 0;
            this.lastStorageAccessTime = now;
        }
        // Check if we're currently throttled
        if (this.isThrottled) {
            if (now - this.throttleStartTime > this.storageCooldownPeriod) {
                this.isThrottled = false;
                this.storageAccessCount = 0;
                return false;
            }
            return true;
        }
        // Check if we've exceeded the limit
        if (this.storageAccessCount >= this.maxStorageAccessPerMinute) {
            this.isThrottled = true;
            this.throttleStartTime = now;
            return true;
        }
        return false;
    }
    /**
     * Schedule batch processing of storage operations
     * @returns {void}
     */
    private scheduleBatch(): void {
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
        }
        // Process immediately if batch is full
        if (this.batchQueue.length >= this.maxBatchSize) {
            this.processBatch();
            return;
        }
        this.batchTimeout = setTimeout(() => {
            this.processBatch();
        }, this.batchDelay);
    }
    /**
     * Process queued batch operations
     * @returns {void}
     */
    private processBatch(): void {
        if (this.batchQueue.length === 0)
            return;
        // Check if storage is completely blocked
        if (this.isStorageCompletelyBlocked()) {
            this.disableStorageAccess();
            this.batchQueue = []; // Clear the queue
            return;
        }
        // Throttle if we're making too many storage calls
        if (this.shouldThrottleStorage()) {
            logger.debug('Storage access throttled, retrying in 1 second');
            setTimeout(() => this.processBatch(), 1000);
            return;
        }
        try {
            // Group operations by type to minimize storage calls
            const setOperations = this.batchQueue.filter(op => op.operation === 'set');
            const clearOperations = this.batchQueue.filter(op => op.operation === 'clear');
            // Process set operations
            setOperations.forEach(op => {
                try {
                    setCookie(op.key, JSON.stringify(op.value));
                    this.cache.set(op.key, op.value);
                }
                catch (error) {
                    // Check if this is a tracking prevention error
                    if (this.isTrackingPreventionError(error)) {
                        this.disableStorageAccess();
                        return; // Don't log tracking prevention errors
                    }
                    logger.security(`Failed to set cookie ${op.key}:`, { "arg0": error });
                }
            });
            // Process clear operations
            clearOperations.forEach(op => {
                try {
                    clearCookie(op.key);
                    this.cache.delete(op.key);
                }
                catch (error) {
                    // Check if this is a tracking prevention error
                    if (this.isTrackingPreventionError(error)) {
                        this.disableStorageAccess();
                        return; // Don't log tracking prevention errors
                    }
                    logger.security(`Failed to clear cookie ${op.key}:`, { "arg0": error });
                }
            });
            // Clear the queue
            this.batchQueue = [];
        }
        catch (error) {
            logger.error('Batch processing failed:', error, {});
        }
    }
    /**
     * Check if error is related to tracking prevention
     * @param {unknown} error - The error to check
     * @returns {boolean} True if error is related to tracking prevention
     */
    private isTrackingPreventionError(error: unknown): boolean {
        if (error instanceof DOMException) {
            return (error.name === 'SecurityError' ||
                error.message.includes('storage') ||
                error.message.includes('blocked') ||
                error.message.includes('tracking'));
        }
        return false;
    }
    /**
     * Check if storage is completely blocked by tracking prevention
     * @returns {boolean} True if storage is completely blocked
     */
    private isStorageCompletelyBlocked(): boolean {
        return isTrackingBlocked();
    }
    /**
     * Disable storage access when tracking is blocked
     * @returns {void}
     */
    private disableStorageAccess(): void {
        this.isThrottled = true;
        this.throttleStartTime = Date.now();
        // Store tracking state in safe storage
        safeStorage.setItem('trackingDisabled', 'true');
        safeStorage.setItem('trackingDisabledReason', 'cookie_service_throttled');
        safeStorage.setItem('trackingDisabledTimestamp', new Date().toISOString());
    }
    /**
     * Optimized getter with caching and storage access throttling
     * @param {string} key - The storage key to retrieve
     * @returns {unknown} The stored value or null if not found/blocked
     */
    private getCachedOrStored(key: string): unknown {
        // Check cache first
        if (this.cache.has(key)) {
            return this.cache.get(key);
        }
        // Check if storage is completely blocked
        if (this.isStorageCompletelyBlocked()) {
            this.disableStorageAccess();
            return null;
        }
        // Throttle storage access
        if (this.shouldThrottleStorage()) {
            return null; // Return null instead of making storage call
        }
        try {
            // Use safe storage methods
            const stored = safeStorage.getItem(key);
            if (stored) {
                const trimmed = stored.trim();
                if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
                    return null;
                }
                const parsed = JSON.parse(trimmed);
                // Cache the result
                this.cache.set(key, parsed);
                return parsed;
            }
        }
        catch (error) {
            // Check if this is a tracking prevention error
            if (this.isTrackingPreventionError(error)) {
                this.disableStorageAccess();
                return null;
            }
            logger.security(`Failed to get cookie ${key}:`, { "arg0": error });
        }
        return null;
    }
    /**
     * Check if storage is available for use
     * @returns {boolean} True if storage is available
     */
    isStorageAvailable(): boolean {
        return !this.isThrottled && !this.isStorageCompletelyBlocked();
    }
    /**
     * Get user preferences with fallback to defaults
     * @returns {UserPreferences} User preferences object
     */
    getUserPreferences(): UserPreferences {
        // If storage is not available, return defaults
        if (!this.isStorageAvailable()) {
            return this.getDefaultPreferences();
        }
        const cached = this.getCachedOrStored(this.preferencesKey);
        if (cached) {
            // Validate the cached data structure
            if (cached && typeof cached === 'object' && 'theme' in cached) {
                return cached as UserPreferences;
            }
        }
        // Return defaults if no valid data
        return this.getDefaultPreferences();
    }
    /**
     * Clear corrupted preferences cookie
     * @returns {void}
     */
    private clearPreferencesCookie(): void {
        try {
            clearCookie(this.preferencesKey);
        }
        catch (clearError) {
            logger.security('Failed to clear corrupted preferences cookie:', { "arg0": clearError });
        }
    }
    /**
     * Set user preferences with batching and caching
     * @param {Partial<UserPreferences>} preferences - Partial preferences to update
     * @returns {void}
     */
    setUserPreferences(preferences: Partial<UserPreferences>): void {
        try {
            const current = this.getUserPreferences();
            const updated = { ...current, ...preferences };
            // Update cache immediately for responsive UI
            this.cache.set(this.preferencesKey, updated);
            // Only attempt to save if storage is available
            if (this.isStorageAvailable()) {
                // Add to batch queue instead of immediate storage access
                this.batchQueue.push({
                    key: this.preferencesKey,
                    value: updated,
                    operation: 'set'
                });
                this.scheduleBatch();
            }
        }
        catch (error) {
            logger.error('Failed to set user preferences:', error, {});
            // Could emit error event for UI handling if needed
        }
    }
    /**
     * Update a specific preference within a category
     * @param {keyof UserPreferences} category - The preference category
     * @param {string} key - The preference key
     * @param {unknown} value - The new value
     * @returns {void}
     */
    updatePreference(category: keyof UserPreferences, key: string, value: unknown): void {
        try {
            const current = this.getUserPreferences();
            const categoryData = current[category] as Record<string, unknown>;
            const updated = {
                ...current,
                [category]: {
                    ...(categoryData || {}),
                    [key]: value
                }
            };
            setCookie(this.preferencesKey, JSON.stringify(updated), 365);
        }
        catch (error) {
            logger.error('Failed to update preference:', error, {});
            // Could emit error event for UI handling if needed
        }
    }
    /**
     * Get user behavior data with validation and fallback
     * @returns {UserBehavior} User behavior object
     */
    getUserBehavior(): UserBehavior {
        try {
            const stored = getCookie(this.behaviorKey);
            if (stored) {
                // Validate JSON before parsing
                const trimmed = stored.trim();
                if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
                    this.clearBehaviorCookie();
                    return this.getDefaultBehavior();
                }
                // Decode URL-encoded data if needed
                let decoded = trimmed;
                try {
                    // Check if the data is URL-encoded
                    if (trimmed.includes('%')) {
                        decoded = decodeURIComponent(trimmed);
                    }
                }
                catch (decodeError) {
                    logger.security('Failed to decode cookie data, trying raw parse:', { "arg0": decodeError });
                    decoded = trimmed;
                }
                const parsed = JSON.parse(decoded);
                // Validate the parsed data structure
                if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.last_visited_pages)) {
                    logger.security('Invalid behavior data structure, clearing cookie');
                    this.clearBehaviorCookie();
                    return this.getDefaultBehavior();
                }
                return parsed;
            }
        }
        catch (error) {
            logger.security('Failed to parse user behavior from cookie:', { "arg0": error });
            this.clearBehaviorCookie();
            // Could emit error event for UI handling if needed
        }
        return this.getDefaultBehavior();
    }
    /**
     * Clear corrupted behavior cookie
     * @returns {void}
     */
    private clearBehaviorCookie(): void {
        try {
            clearCookie(this.behaviorKey);
        }
        catch (clearError) {
            logger.security('Failed to clear corrupted behavior cookie:', { "arg0": clearError });
        }
    }
    /**
     * Update user behavior data
     * @param {Partial<UserBehavior>} updates - Partial behavior updates
     * @returns {void}
     */
    updateUserBehavior(updates: Partial<UserBehavior>): void {
        try {
            const current = this.getUserBehavior();
            const updated = { ...current, ...updates };
            setCookie(this.behaviorKey, JSON.stringify(updated), 365);
        }
        catch (error) {
            logger.error('Failed to update user behavior:', error, {});
            // Could emit error event for UI handling if needed
        }
    }
    /**
     * Track a page visit in user behavior
     * @param {string} page - The page URL or identifier
     * @returns {void}
     */
    trackPageVisit(page: string): void {
        const behavior = this.getUserBehavior();
        const lastVisited = behavior.last_visited_pages.filter(p => p !== page);
        lastVisited.unshift(page);
        this.updateUserBehavior({
            last_visited_pages: lastVisited.slice(0, 10) // Keep last 10 pages
        });
    }
    /**
     * Track a search query in user behavior
     * @param {string} query - The search query
     * @returns {void}
     */
    trackSearchQuery(query: string): void {
        const behavior = this.getUserBehavior();
        const searchHistory = behavior.search_history.filter(q => q !== query);
        searchHistory.unshift(query);
        this.updateUserBehavior({
            search_history: searchHistory.slice(0, 20) // Keep last 20 searches
        });
    }
    /**
     * Track training progress for a course
     * @param {string} courseId - The course identifier
     * @param {number} progress - Progress percentage (0-100)
     * @returns {void}
     */
    trackTrainingProgress(courseId: string, progress: number): void {
        const behavior = this.getUserBehavior();
        this.updateUserBehavior({
            training_progress: {
                ...behavior.training_progress,
                [courseId]: progress
            }
        });
    }
    /**
     * Get session data with tracking prevention handling
     * @returns {SessionData | null} Session data or null if not available
     */
    getSessionData(): SessionData | null {
        try {
            // If storage is not available, return null
            if (!this.isStorageAvailable()) {
                return null;
            }
            const cached = this.getCachedOrStored(this.sessionKey);
            if (cached) {
                // Validate the cached data structure
                if (cached && typeof cached === 'object' && 'session_id' in cached) {
                    return cached as SessionData;
                }
            }
            return null;
        }
        catch (error) {
            // Check if this is a tracking prevention error
            if (error instanceof DOMException && (error.name === 'SecurityError' ||
                error.message.includes('storage') ||
                error.message.includes('blocked'))) {
                logger.security('Tracking prevention blocked cookie access:', { "arg0": error.message });
                // Don't clear cookies in this case as it's not a corruption issue
                return null;
            }
            logger.security('Failed to parse session data from cookie:', { "arg0": error });
            logger.security('Cookie content that caused error:', { "arg0": getCookie(this.sessionKey) });
            // Clear the corrupted cookie only if it's not a tracking prevention issue
            this.clearSessionCookie();
        }
        return null;
    }
    /**
     * Set session data with batching and caching
     * @param {SessionData} sessionData - The session data to store
     * @returns {void}
     */
    setSessionData(sessionData: SessionData): void {
        try {
            // If storage is not available, just update cache
            if (!this.isStorageAvailable()) {
                this.cache.set(this.sessionKey, sessionData);
                return;
            }
            // Add to batch queue instead of immediate storage access
            this.batchQueue.push({
                key: this.sessionKey,
                value: sessionData,
                operation: 'set'
            });
            // Update cache immediately for responsive UI
            this.cache.set(this.sessionKey, sessionData);
            this.scheduleBatch();
        }
        catch (error) {
            logger.auth('Failed to set session data:', { "arg0": error });
        }
    }
    /**
     * Clear session data from cache and storage
     * @returns {void}
     */
    clearSessionData(): void {
        try {
            // Clear from cache
            this.cache.delete(this.sessionKey);
            // If storage is available, add to batch queue
            if (this.isStorageAvailable()) {
                this.batchQueue.push({
                    key: this.sessionKey,
                    value: null,
                    operation: 'clear'
                });
                this.scheduleBatch();
            }
        }
        catch (error) {
            logger.auth('Failed to clear session data:', { "arg0": error });
        }
    }
    /**
     * Clear corrupted session cookie
     * @returns {void}
     */
    private clearSessionCookie(): void {
        try {
            clearCookie(this.sessionKey);
            logger.security('Session cookie cleared successfully');
        }
        catch (clearError) {
            logger.security('Failed to clear corrupted session cookie:', { "arg0": clearError });
        }
    }
    /**
     * Create a new session with device and referral information
     * @param {string} [referralSource] - Optional referral source
     * @param {Record<string, string>} [utmParams] - Optional UTM parameters
     * @returns {SessionData} The created session data
     */
    createSession(referralSource?: string, utmParams?: Record<string, string>): SessionData {
        const sessionData: SessionData = {
            session_id: this.generateSessionId(),
            start_time: Date.now(),
            last_activity: Date.now(),
            page_views: 0,
            events_triggered: [],
            referral_source: referralSource || document.referrer || '',
            utm_parameters: utmParams || this.extractUTMParameters(),
            device_info: this.getDeviceInfo()
        };
        try {
            const jsonString = JSON.stringify(sessionData);
            // Check if the cookie would be too large (browsers typically limit cookies to 4KB)
            if (jsonString.length > 3500) {
                logger.auth('Session data too large, creating minimal session');
                const minimalSession: SessionData = {
                    session_id: sessionData.session_id,
                    start_time: sessionData.start_time,
                    last_activity: sessionData.last_activity,
                    page_views: 0,
                    events_triggered: [],
                    referral_source: '',
                    utm_parameters: {},
                    device_info: {
                        user_agent: navigator.userAgent.substring(0, 100), // Truncate user agent
                        screen_resolution: `${screen.width}x${screen.height}`,
                        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
                        device_type: 'desktop'
                    }
                };
                setCookie(this.sessionKey, JSON.stringify(minimalSession), 1);
                return minimalSession;
            }
            setCookie(this.sessionKey, jsonString, 1); // 1 day
            return sessionData;
        }
        catch (error) {
            logger.security('Failed to create session cookie:', { "arg0": error });
            // Return session data without storing cookie if there's an error
            return sessionData;
        }
    }
    /**
     * Update session data with size optimization
     * @param {Partial<SessionData>} updates - Partial session updates
     * @returns {void}
     */
    updateSession(updates: Partial<SessionData>): void {
        const current = this.getSessionData();
        if (current) {
            const updated = { ...current, ...updates, last_activity: Date.now() };
            // Check cookie size and truncate if necessary
            const sessionJson = JSON.stringify(updated);
            const maxCookieSize = 3000; // Maximum cookie size in characters
            if (sessionJson.length > maxCookieSize) {
                // Truncate events_triggered array if cookie is too large
                if (updated.events_triggered && updated.events_triggered.length > 10) {
                    updated.events_triggered = updated.events_triggered.slice(-10);
                }
                // If still too large, clear events entirely
                const truncatedJson = JSON.stringify(updated);
                if (truncatedJson.length > maxCookieSize) {
                    updated.events_triggered = [];
                }
            }
            setCookie(this.sessionKey, JSON.stringify(updated), 1);
        }
    }
    /**
     * Track a session event with size limits
     * @param {string} eventName - The event name to track
     * @returns {void}
     */
    trackSessionEvent(eventName: string): void {
        const session = this.getSessionData();
        if (session) {
            // Limit events_triggered array to prevent cookie size issues
            const maxEvents = 50; // Maximum number of events to store
            const currentEvents = session.events_triggered || [];
            // Add new event and limit array size
            const updatedEvents = [...currentEvents, eventName].slice(-maxEvents);
            this.updateSession({
                events_triggered: updatedEvents
            });
        }
    }
    /**
     * Increment page views counter in session
     * @returns {void}
     */
    incrementPageViews(): void {
        const session = this.getSessionData();
        if (session) {
            this.updateSession({
                page_views: session.page_views + 1
            });
        }
    }
    /**
     * Get personalization data with validation
     * @returns {PersonalizationData} Personalization data object
     */
    getPersonalizationData(): PersonalizationData {
        try {
            const stored = getCookie(this.personalizationKey);
            if (stored) {
                // Validate JSON before parsing
                const trimmed = stored.trim();
                if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
                    this.clearPersonalizationCookie();
                    return this.getDefaultPersonalization();
                }
                const parsed = JSON.parse(trimmed);
                // Validate the parsed data structure
                if (!parsed || typeof parsed !== 'object') {
                    logger.security('Invalid personalization data structure, clearing cookie');
                    this.clearPersonalizationCookie();
                    return this.getDefaultPersonalization();
                }
                return parsed;
            }
        }
        catch (error) {
            logger.security('Failed to parse personalization data from cookie:', { "arg0": error });
            // Clear the corrupted cookie
            this.clearPersonalizationCookie();
        }
        return this.getDefaultPersonalization();
    }
    /**
     * Clear corrupted personalization cookie
     * @returns {void}
     */
    private clearPersonalizationCookie(): void {
        try {
            clearCookie(this.personalizationKey);
        }
        catch (clearError) {
            logger.security('Failed to clear corrupted personalization cookie:', { "arg0": clearError });
        }
    }
    /**
     * Update personalization data
     * @param {Partial<PersonalizationData>} updates - Partial personalization updates
     * @returns {void}
     */
    updatePersonalization(updates: Partial<PersonalizationData>): void {
        try {
            const current = this.getPersonalizationData();
            const updated = { ...current, ...updates };
            setCookie(this.personalizationKey, JSON.stringify(updated), 365);
        }
        catch (error) {
            logger.error('Failed to update personalization data:', error, {});
        }
    }
    /**
     * Get comprehensive analytics data combining all user data
     * @returns {object} Analytics data with preferences, behavior, session, and insights
     */
    getAnalyticsData(): {
        preferences: UserPreferences;
        behavior: UserBehavior;
        session: SessionData;
        personalization: PersonalizationData;
        insights: {
            engagement_level: 'low' | 'medium' | 'high';
            learning_preferences: object;
            content_recommendations: string[];
            user_journey_stage: string;
            optimization_opportunities: string[];
        };
    } {
        const preferences = this.getUserPreferences();
        const behavior = this.getUserBehavior();
        let session = this.getSessionData();
        const personalization = this.getPersonalizationData();
        // Ensure session exists
        if (!session) {
            session = this.createSession();
        }
        // Initialize with sample data if behavior is empty
        if (behavior.last_visited_pages.length === 0) {
            this.updateUserBehavior({
                last_visited_pages: ['/analytics', '/preferences', '/faq', '/about'],
                search_history: ['police training', 'firearms certification'],
                training_progress: { 'course_001': 75, 'course_002': 30 },
                time_spent_on_site: 2400000, // 40 minutes
                preferred_content_types: ['video', 'interactive']
            });
        }
        return {
            preferences,
            behavior: this.getUserBehavior(), // Get updated behavior
            session,
            personalization,
            insights: this.generateInsights(preferences, this.getUserBehavior(), session, personalization)
        };
    }
    /**
     * Get default user preferences
     * @returns {UserPreferences} Default preferences object
     */
    private getDefaultPreferences(): UserPreferences {
        return {
            theme: 'auto',
            language: navigator.language || 'en-US',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            dateFormat: 'MM/DD/YYYY',
            currency: 'USD',
            notifications: {
                email: true,
                sms: false,
                push: false,
                training_reminders: true,
                event_updates: true,
                marketing: false
            },
            training: {
                preferred_topics: ['firearms training', 'safety protocols', 'law enforcement'],
                learning_style: 'visual',
                difficulty_level: 'beginner',
                session_duration: 30,
                auto_play_videos: false,
                show_transcripts: true
            },
            accessibility: {
                font_size: 'medium',
                high_contrast: false,
                reduce_motion: false,
                screen_reader_friendly: false
            },
            privacy: {
                profile_visibility: 'private',
                show_online_status: false,
                allow_messaging: true,
                data_sharing: false
            }
        };
    }
    /**
     * Get default user behavior data
     * @returns {UserBehavior} Default behavior object
     */
    private getDefaultBehavior(): UserBehavior {
        return {
            last_visited_pages: [],
            favorite_categories: [],
            search_history: [],
            training_progress: {},
            time_spent_on_site: 0,
            preferred_content_types: [],
            device_preferences: {
                mobile_optimized: false,
                desktop_preferred: true
            }
        };
    }
    /**
     * Get default personalization data
     * @returns {PersonalizationData} Default personalization object
     */
    private getDefaultPersonalization(): PersonalizationData {
        return {
            user_segment: 'new_user',
            interests: ['law enforcement', 'firearms training', 'safety protocols'],
            behavior_patterns: ['morning_learner', 'video_preference'],
            recommended_content: ['Basic Firearms Safety', 'Law Enforcement Fundamentals'],
            personalized_features: ['adaptive_learning', 'progress_tracking']
        };
    }
    /**
     * Generate a unique session ID
     * @returns {string} Unique session identifier
     */
    private generateSessionId(): string {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    /**
     * Extract UTM parameters from URL
     * @returns {Record<string, string>} UTM parameters object
     */
    private extractUTMParameters(): Record<string, string> {
        const urlParams = new URLSearchParams(window.location.search);
        const utmParams: Record<string, string> = {};
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
            const value = urlParams.get(param);
            if (value) {
                utmParams[param] = value;
            }
        });
        return utmParams;
    }
    /**
     * Get device information for session tracking
     * @returns {object} Device information object
     */
    private getDeviceInfo(): {
        user_agent: string;
        screen_resolution: string;
        viewport_size: string;
        device_type: 'mobile' | 'tablet' | 'desktop';
    } {
        const userAgent = navigator.userAgent;
        const screenResolution = `${window.screen.width}x${window.screen.height}`;
        const viewportSize = `${window.innerWidth}x${window.innerHeight}`;
        let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
            deviceType = window.innerWidth < 768 ? 'mobile' : 'tablet';
        }
        return {
            user_agent: userAgent,
            screen_resolution: screenResolution,
            viewport_size: viewportSize,
            device_type: deviceType
        };
    }
    /**
     * Generate user insights from collected data
     * @param {UserPreferences} preferences - User preferences
     * @param {UserBehavior} behavior - User behavior data
     * @param {SessionData | null} session - Current session data
     * @param {PersonalizationData} personalization - Personalization data
     * @returns {object} Generated insights object
     */
    private generateInsights(preferences: UserPreferences, behavior: UserBehavior, session: SessionData | null, personalization: PersonalizationData): {
        engagement_level: 'low' | 'medium' | 'high';
        learning_preferences: object;
        content_recommendations: string[];
        user_journey_stage: string;
        optimization_opportunities: string[];
    } {
        return {
            engagement_level: this.calculateEngagementLevel(behavior, session),
            learning_preferences: this.analyzeLearningPreferences(preferences, behavior),
            content_recommendations: this.generateContentRecommendations(behavior, personalization),
            user_journey_stage: this.determineUserJourneyStage(behavior, session),
            optimization_opportunities: this.identifyOptimizationOpportunities(preferences, behavior)
        };
    }
    /**
     * Calculate user engagement level based on behavior and session data
     * @param {UserBehavior} behavior - User behavior data
     * @param {SessionData | null} session - Current session data
     * @returns {'low' | 'medium' | 'high'} Engagement level
     */
    private calculateEngagementLevel(behavior: UserBehavior, session: SessionData | null): 'low' | 'medium' | 'high' {
        if (!session) {
            // Calculate based on behavior data if no session
            const totalTimeSpent = behavior.time_spent_on_site;
            const totalPageViews = behavior.last_visited_pages.length;
            if (totalTimeSpent > 300000 && totalPageViews > 5)
                return 'high';
            if (totalTimeSpent > 60000 && totalPageViews > 2)
                return 'medium';
            return 'low';
        }
        const timeSpent = session.last_activity - session.start_time;
        const pageViews = session.page_views;
        if (timeSpent > 300000 && pageViews > 5)
            return 'high'; // 5+ minutes, 5+ pages
        if (timeSpent > 60000 && pageViews > 2)
            return 'medium'; // 1+ minutes, 2+ pages
        return 'low';
    }
    /**
     * Analyze learning preferences from user data
     * @param {UserPreferences} preferences - User preferences
     * @param {UserBehavior} behavior - User behavior data
     * @returns {object} Learning preferences analysis
     */
    private analyzeLearningPreferences(preferences: UserPreferences, behavior: UserBehavior): object {
        return {
            preferred_duration: preferences.training.session_duration,
            learning_style: preferences.training.learning_style,
            content_types: behavior.preferred_content_types,
            engagement_patterns: behavior.last_visited_pages
        };
    }
    /**
     * Generate content recommendations based on behavior and personalization
     * @param {UserBehavior} behavior - User behavior data
     * @param {PersonalizationData} personalization - Personalization data
     * @returns {string[]} Array of content recommendations
     */
    private generateContentRecommendations(behavior: UserBehavior, personalization: PersonalizationData): string[] {
        // Simple recommendation logic - can be enhanced with ML
        const recommendations = [...personalization.recommended_content];
        if (behavior.favorite_categories.length > 0) {
            recommendations.push(...behavior.favorite_categories);
        }
        // Add sample recommendations based on behavior
        if (behavior.search_history.includes('firearms')) {
            recommendations.push('Firearms Safety Training', 'Advanced Marksmanship');
        }
        if (behavior.search_history.includes('police training')) {
            recommendations.push('Law Enforcement Fundamentals', 'Community Policing');
        }
        if (behavior.preferred_content_types.includes('video')) {
            recommendations.push('Video-based Training Modules');
        }
        if (behavior.preferred_content_types.includes('interactive')) {
            recommendations.push('Interactive Scenarios', 'Virtual Reality Training');
        }
        return [...new Set(recommendations)]; // Remove duplicates
    }
    /**
     * Determine user journey stage based on behavior and session data
     * @param {UserBehavior} behavior - User behavior data
     * @param {SessionData | null} session - Current session data
     * @returns {string} User journey stage
     */
    private determineUserJourneyStage(behavior: UserBehavior, session: SessionData | null): string {
        let totalTimeSpent = behavior.time_spent_on_site;
        let totalPageViews = behavior.last_visited_pages.length;
        if (session) {
            totalTimeSpent += (session.last_activity - session.start_time);
            totalPageViews += session.page_views;
        }
        if (totalTimeSpent > 1800000 && totalPageViews > 20)
            return 'power_user'; // 30+ minutes, 20+ pages
        if (totalTimeSpent > 300000 && totalPageViews > 5)
            return 'engaged_user'; // 5+ minutes, 5+ pages
        if (totalTimeSpent > 60000)
            return 'returning_visitor'; // 1+ minute
        return 'new_visitor';
    }
    /**
     * Identify optimization opportunities based on user data
     * @param {UserPreferences} preferences - User preferences
     * @param {UserBehavior} behavior - User behavior data
     * @returns {string[]} Array of optimization opportunities
     */
    private identifyOptimizationOpportunities(preferences: UserPreferences, behavior: UserBehavior): string[] {
        const opportunities = [];
        if (preferences.training.session_duration < 15) {
            opportunities.push('increase_session_duration');
        }
        if (behavior.last_visited_pages.length < 3) {
            opportunities.push('encourage_exploration');
        }
        if (!preferences.notifications.training_reminders) {
            opportunities.push('enable_reminders');
        }
        return opportunities;
    }
    /**
     * Clear all APT cookies (for debugging purposes)
     * @returns {void}
     */
    clearAllCookies(): void {
        try {
            const cookies = document.cookie.split(';');
            cookies.forEach(cookie => {
                const [name] = cookie.split('=');
                const trimmedName = name.trim();
                if (trimmedName.startsWith(this.cookiePrefix)) {
                    logger.security('Clearing cookie:', { "arg0": trimmedName });
                    clearCookie(trimmedName);
                }
            });
            logger.debug('All APT cookies cleared');
        }
        catch (error) {
            logger.error('Failed to clear all cookies:', error, {});
        }
    }
    /**
     * Cleanup expired data to maintain performance
     * @returns {void}
     */
    cleanupExpiredData(): void {
        // Remove old search history entries (older than 30 days)
        const behavior = this.getUserBehavior();
        if (behavior.search_history.length > 50) {
            this.updateUserBehavior({
                search_history: behavior.search_history.slice(0, 30)
            });
        }
    }
    /**
     * Get user search history
     * @returns {string[]} Array of search queries
     */
    getSearchHistory(): string[] {
        const behavior = this.getUserBehavior();
        return behavior.search_history || [];
    }
    /**
     * Get current session page views count
     * @returns {number} Number of page views in current session
     */
    getPageViewsCount(): number {
        const session = this.getSessionData();
        return session ? session.page_views : 0;
    }
    /**
     * Export user data for portability
     * @returns {string} JSON string of all user data
     */
    exportUserData(): string {
        const data = this.getAnalyticsData();
        return JSON.stringify(data, null, 2);
    }
    /**
     * Import user data from JSON string
     * @param {string} dataString - JSON string of user data
     * @returns {boolean} True if import was successful
     */
    importUserData(dataString: string): boolean {
        try {
            const data = JSON.parse(dataString);
            if (data.preferences)
                this.setUserPreferences(data.preferences);
            if (data.behavior)
                this.updateUserBehavior(data.behavior);
            if (data.personalization)
                this.updatePersonalization(data.personalization);
            return true;
        }
        catch (error) {
            logger.error('Failed to import user data:', error, {});
            return false;
        }
    }
    /**
     * Cleanup resources and event listeners
     * @returns {void}
     */
    cleanup(): void {
        // Clear batch timeout
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
            this.batchTimeout = null;
        }
        // Clear cache
        this.cache.clear();
        // Clear batch queue
        this.batchQueue = [];
        // Execute cleanup listeners
        this.cleanupListeners.forEach(cleanup => {
            try {
                cleanup();
            }
            catch (error) {
                logger.error('Error during cleanup:', error, {});
            }
        });
        this.cleanupListeners = [];
        // Reset throttling state
        this.isThrottled = false;
        this.storageAccessCount = 0;
        this.lastStorageAccessTime = Date.now();
        this.throttleStartTime = 0;
    }
    /**
     * Add cleanup listener for resource management
     * @param {() => void} cleanup - Cleanup function to call
     * @returns {void}
     */
    addCleanupListener(cleanup: () => void): void {
        this.cleanupListeners.push(cleanup);
    }
    /**
     * Initialize service with performance optimizations
     * @returns {void}
     */
    initialize(): void {
        // Handle page visibility changes for performance
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is hidden, process any pending batches
                if (this.batchQueue.length > 0) {
                    this.processBatch();
                }
            }
        };
        // Add visibility change listener
        document.addEventListener('visibilitychange', handleVisibilityChange);
        // Add cleanup listener to remove event listener
        this.addCleanupListener(() => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        });
        // Handle page unload to save any pending data
        const handleBeforeUnload = () => {
            if (this.batchQueue.length > 0) {
                this.processBatch();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        // Add cleanup listener to remove event listener
        this.addCleanupListener(() => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        });
    }
}
const cookieService = new CookieService();
// Initialize the service with performance optimizations
cookieService.initialize();
export default cookieService;
